// UI-P3 (2026-05-30) — Trade Classification & Action Framework section.
//
// Renders the existing Phase 12CH `/api/v1/signal/trade-classification/*`
// engine output inside The Signal scorecard. Mounted into SignalModule.tsx
// (internal code id preserved; user-visible copy reads The Signal).
//
// Modes:
//   * Default mode (no `symbol` prop) → renders the seven-category catalogue
//     and today's per-category counts using
//     /api/v1/signal/trade-classification/categories +
//     /api/v1/signal/trade-classification/summary?date=YYYY-MM-DD.
//   * Symbol mode (`symbol` prop set) → renders a per-symbol scorecard via
//     /api/v1/signal/trade-classification/{symbol}?date=YYYY-MM-DD.
//
// Banned user-facing language: Buy / Sell / Short / Enter here / Exit here /
//                              Set stop / Position size / Guaranteed.
// Safe labels: Observed opportunity / Possible holding horizon /
//              What would need to hold true / What would weaken the setup /
//              Follow-through to monitor / Educational framework only.
//
// Read-only. Does not place orders. Does not change official Signal labels,
// the official Signal hit rate, Phase 13 readiness, or Candidate Promotion.
import { useEffect, useState } from "react";
import { Activity, AlertTriangle, BookOpen, Compass, Layers, RefreshCw, ShieldCheck } from "lucide-react";
import { apiV1Base } from "../../lib/apiConfig";
import { SectionExplainer, TermHelp } from "../BeginnerHelp";

const POLL_MS = 60_000;

interface TradeClassCategoryItem {
  id: string;
  label: string;
  holding_horizon: string;
  description: string;
}

interface TradeClassCategoriesPayload {
  status: string;
  categories: TradeClassCategoryItem[];
  educational_disclaimer?: string;
  advisory_only?: boolean;
  educational_only?: boolean;
  no_buy_sell_language?: boolean;
  no_buy_sell_instruction?: boolean;
  no_order_action?: boolean;
  does_not_place_orders?: boolean;
  affects_official_hit_rate?: boolean;
  affects_official_signal_hit_rate?: boolean;
}

interface TradeClassSummaryPayload {
  status: string;
  date?: string;
  candidate_count?: number;
  counts?: Record<string, number>;
  labels?: Record<string, string>;
  advisory_only?: boolean;
  no_buy_sell_instruction?: boolean;
  no_order_action?: boolean;
  affects_official_signal_hit_rate?: boolean;
}

interface TradeClassSymbolPayload {
  status: string;
  symbol?: string;
  date?: string;
  trade_classification?: string | null;
  trade_type?: string | null;
  trade_type_label?: string | null;
  holding_horizon?: string | null;
  category_description?: string | null;
  observed_opportunity?: string | null;
  action_framework?: string[];
  profit_management_concept?: string[];
  failure_conditions?: string[];
  follow_through_assessment?: string | null;
  advisory_only?: boolean;
  no_buy_sell_instruction?: boolean;
  no_order_action?: boolean;
  does_not_place_orders?: boolean;
  affects_official_signal_hit_rate?: boolean;
  educational_disclaimer?: string | null;
  // Phase 12CH-bis (2026-05-31) — lane-aware fields
  lane_context?: string | null;
  why_this_classification?: string | null;
  educational_framework_only?: boolean;
}

// ── envelope shape ──
interface Envelope<T> { ok: boolean; data: T | null; error: { message: string; code: string } | null }

async function fetchTC<T>(path: string): Promise<Envelope<T>> {
  try {
    const res = await fetch(`${apiV1Base()}${path}`, { credentials: "include", headers: { accept: "application/json" }, cache: "no-store" });
    if (!res.ok) return { ok: false, data: null, error: { message: `HTTP ${res.status}`, code: String(res.status) } };
    const body = (await res.json()) as Envelope<T>;
    return body;
  } catch (e) {
    return { ok: false, data: null, error: { message: String(e), code: "FETCH" } };
  }
}

function todayIsoDate(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())).toISOString().slice(0, 10);
}

function fmtEtDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T12:00:00Z");
    if (isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", year: "numeric", month: "short", day: "2-digit" }).format(d) + " ET";
  } catch { return iso; }
}

// ── shared chrome ──
function AdvisoryFooter() {
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 text-amber-400 px-3 py-2 text-[11px] mt-3">
      <strong className="uppercase tracking-widest">Educational framework only.</strong>{" "}
      This section is research intelligence only. It never provides order instructions, never connects to order execution, and never changes the official Signal label or hit rate.
    </div>
  );
}

function SafeLabelGlossary() {
  return (
    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] text-[var(--muted-text)]">
      <div>· "Observed opportunity" — what the data showed.</div>
      <div>· "Possible holding horizon" — typical horizon for this classification.</div>
      <div>· "What would need to hold true" — preconditions for the setup.</div>
      <div>· "What would weaken the setup" — failure conditions to monitor.</div>
      <div>· "Follow-through to monitor" — what to watch next.</div>
      <div>· "Educational framework only" — never an instruction.</div>
    </div>
  );
}

// ── default catalogue + today distribution mode ──
function CategoryDistribution({ counts, labels, date, total }: { counts: Record<string, number>; labels: Record<string, string>; date?: string; total: number }) {
  const ids = Object.keys(labels);
  if (!ids.length) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[var(--muted-text)] mb-2">Today's distribution · {fmtEtDate(date)} · {total} candidates</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {ids.map((id) => (
          <div key={id} className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] px-2 py-2">
            <div className="text-[9px] uppercase tracking-widest text-[var(--muted-text)]">{labels[id]}</div>
            <div className="text-lg font-bold font-mono">{counts[id] ?? 0}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoryCatalogue({ items }: { items: TradeClassCategoryItem[] }) {
  return (
    <div className="space-y-2">
      <div className="text-[11px] uppercase tracking-widest text-[var(--muted-text)]">Seven advisory classifications</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {items.map((c) => (
          <div key={c.id} className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-bold text-[var(--app-text)]">{c.label}</div>
              <span className="inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] uppercase tracking-widest border-[var(--card-border)] text-[var(--muted-text)]">{c.holding_horizon}</span>
            </div>
            <div className="mt-1 text-[12px] text-[var(--muted-text)]">{c.description}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── symbol scorecard mode ──
function SymbolScorecard({ d }: { d: TradeClassSymbolPayload }) {
  const cls = d.trade_classification || d.trade_type || "OBSERVATION_ONLY";
  const label = d.trade_type_label || cls.replace(/_/g, " ");
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Trade Classification</div>
          <div className="text-xl font-black italic uppercase text-[var(--app-text)]">{label}</div>
          {d.lane_context && (
            <div className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-widest border-emerald-500/30 bg-emerald-500/5 text-emerald-400" data-testid="trade-class-lane-context">
              Lane: {d.lane_context}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-[var(--muted-text)]">
          <div><strong>Possible holding horizon:</strong> {d.holding_horizon || "—"}</div>
          <div><strong>Date:</strong> {fmtEtDate(d.date)}</div>
        </div>
      </div>

      <div className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3">
        <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">Observed opportunity</div>
        <div className="text-sm text-[var(--app-text)]">{d.observed_opportunity || "No observed opportunity recorded."}</div>
      </div>

      {d.why_this_classification && (
        <div className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3" data-testid="trade-class-why">
          <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">Why this classification</div>
          <div className="text-sm text-[var(--app-text)]">{d.why_this_classification}</div>
          <div className="mt-2 text-[10px] text-[var(--muted-text)]">Lane routing does not change official Signal labels or hit rate. This is research intelligence only and does not place trades.</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3">
          <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">What would need to hold true</div>
          {Array.isArray(d.action_framework) && d.action_framework.length > 0 ? (
            <ul className="list-disc pl-4 text-xs text-[var(--app-text)] space-y-1">
              {d.action_framework.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          ) : <div className="text-xs text-[var(--muted-text)]">No action framework recorded.</div>}
        </div>
        <div className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3">
          <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">Profit-management concept</div>
          {Array.isArray(d.profit_management_concept) && d.profit_management_concept.length > 0 ? (
            <ul className="list-disc pl-4 text-xs text-[var(--app-text)] space-y-1">
              {d.profit_management_concept.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          ) : <div className="text-xs text-[var(--muted-text)]">No profit-management concept recorded.</div>}
        </div>
        <div className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3">
          <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">What would weaken the setup</div>
          {Array.isArray(d.failure_conditions) && d.failure_conditions.length > 0 ? (
            <ul className="list-disc pl-4 text-xs text-[var(--app-text)] space-y-1">
              {d.failure_conditions.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          ) : <div className="text-xs text-[var(--muted-text)]">No failure conditions recorded.</div>}
        </div>
      </div>

      <div className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3">
        <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">Follow-through to monitor</div>
        <div className="text-sm text-[var(--app-text)]">{d.follow_through_assessment || "No follow-through assessment recorded."}</div>
      </div>
    </div>
  );
}

export interface TradeClassificationFrameworkProps {
  symbol?: string;
}

export default function TradeClassificationFramework({ symbol }: TradeClassificationFrameworkProps) {
  const [date] = useState<string>(todayIsoDate());
  const [catalogue, setCatalogue] = useState<TradeClassCategoryItem[]>([]);
  const [disclaimer, setDisclaimer] = useState<string>("");
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [total, setTotal] = useState<number>(0);
  const [symbolPayload, setSymbolPayload] = useState<TradeClassSymbolPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  async function fetchAll() {
    setLoading(true);
    setError(null);
    try {
      // categories — always needed for the catalogue display
      const cat = await fetchTC<TradeClassCategoriesPayload>("/signal/trade-classification/categories");
      if (cat.ok && cat.data) {
        setCatalogue(cat.data.categories || []);
        if (cat.data.educational_disclaimer) setDisclaimer(cat.data.educational_disclaimer);
      }
      if (symbol) {
        const ss = await fetchTC<TradeClassSymbolPayload>(`/signal/trade-classification/${encodeURIComponent(symbol)}?date=${date}`);
        if (ss.ok && ss.data) setSymbolPayload(ss.data);
        else if (ss.error) setError(ss.error.message);
      } else {
        const sm = await fetchTC<TradeClassSummaryPayload>(`/signal/trade-classification/summary?date=${date}`);
        if (sm.ok && sm.data) {
          setCounts(sm.data.counts || {});
          setLabels(sm.data.labels || {});
          setTotal(sm.data.candidate_count || 0);
        } else if (sm.error) {
          setError(sm.error.message);
        }
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, POLL_MS);
    return () => clearInterval(t);
  }, [symbol, date]);

  return (
    <div
      className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4 shadow-sm"
      data-testid="trade-classification-framework"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <div className="bg-emerald-500/10 border border-emerald-500/30 p-2 rounded-lg text-emerald-500">
            <Compass className="h-4 w-4" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--muted-text)]">The Signal · Educational</div>
            <h2 className="text-lg font-black uppercase italic text-[var(--app-text)] tracking-tight">Trade Classification &amp; Action Framework</h2>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[var(--muted-text)]">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Advisory only · no order action</span>
          <button onClick={fetchAll} className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--app-text)] hover:text-emerald-500">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <SectionExplainer
        testId="beginner-help-trade-classification"
        title="Trade Classification beginner guide"
        what={<>Trade Classification is an educational category for the kind of setup being reviewed in The Signal.</>}
        why={<>It gives beginners a plain-English frame for the setup without changing the underlying Signal result or methodology.</>}
        lookAt={<>Look at the classification name, possible holding horizon, what would need to hold true, what would weaken the setup, and follow-through to monitor.</>}
        notDo={<>It does not tell you what order to place, does not change official Signal labels, and does not affect the official Signal hit rate.</>}
      >
        <TermHelp term="Trade Classification" />
        <TermHelp term="Follow-Through" />
        <TermHelp term="The Signal" />
        <TermHelp term="Official Signal labels" />
      </SectionExplainer>

      {symbol ? (
        symbolPayload ? <SymbolScorecard d={symbolPayload} /> : (
          <div className="text-sm text-[var(--muted-text)]"><Activity className="inline w-4 h-4 mr-2"/>Loading trade classification for {symbol}…</div>
        )
      ) : (
        <div className="space-y-4">
          {catalogue.length > 0 && <CategoryCatalogue items={catalogue} />}
          {Object.keys(labels).length > 0 && <CategoryDistribution counts={counts} labels={labels} date={date} total={total} />}
        </div>
      )}

      {disclaimer && (
        <div className="mt-3 rounded-lg border border-[var(--card-border)] bg-[var(--input-bg)] px-3 py-2 text-[11px] text-[var(--muted-text)]">
          <BookOpen className="inline w-3 h-3 mr-1.5" /> {disclaimer}
        </div>
      )}

      <AdvisoryFooter />
      <SafeLabelGlossary />

      {error && (
        <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/5 text-rose-400 px-3 py-2 text-[11px]">
          <AlertTriangle className="inline w-3 h-3 mr-1.5" /> {error}
        </div>
      )}
    </div>
  );
}
